import { RefObject, useLayoutEffect } from 'react';

/** Set on the shell's `<main>`; forge-theme.css widens a `fit` page's root to it. */
const FIT_WIDTH_PROPERTY = '--forge-page-fit-width';

/** While it is on `<main>`, forge-theme.css lays every table out at its natural width. */
export const MEASURING_CLASS = 'forge-page-measuring';

/** The page root holding the table, and the nearest box below it that clips the table. */
function boxesOf(table: HTMLElement, main: HTMLElement) {
  let clip: HTMLElement | null = null;
  let root = table;
  while (root.parentElement && root.parentElement !== main) {
    root = root.parentElement;
    if (!clip && getComputedStyle(root).overflowX !== 'visible') clip = root;
  }
  return { root, clip: clip ?? root };
}

/**
 * Widens a `fit` page's root as far as needed for every visible table in `main` to show all its
 * columns at their natural width. It measures with the root at the cap: each table is laid out at
 * its max-content width, and the width its clipping box would then have to scroll is added to the
 * root's, since a box in the page's flow grows as much as the root does. The extra pixel covers
 * scrollWidth's rounding, which would otherwise leave a column a fraction short and wrap it.
 */
export function fitPageWidth(main: HTMLElement): void {
  const tables = Array.from(main.getElementsByTagName('table')).filter(
    (table) => table.getClientRects().length > 0
  );
  const boxes = tables.map((table) => boxesOf(table, main));
  // Tables at their natural width can be shorter, which would scroll the page up.
  const scrollers = [main, ...boxes.map(({ clip }) => clip)];
  const scrolled = scrollers.map(({ scrollTop, scrollLeft }) => ({ scrollTop, scrollLeft }));

  main.style.setProperty(FIT_WIDTH_PROPERTY, '0px');
  main.classList.add(MEASURING_CLASS);
  const needed = boxes.map(({ root, clip }) => {
    const overflow = clip.scrollWidth - clip.clientWidth;
    return root.getBoundingClientRect().width + (overflow > 0 ? overflow + 1 : 0);
  });
  main.classList.remove(MEASURING_CLASS);
  main.style.setProperty(FIT_WIDTH_PROPERTY, `${Math.ceil(Math.max(0, ...needed))}px`);
  scrollers.forEach((scroller, index) => Object.assign(scroller, scrolled[index]));
}

function holdsTable(node: Node): boolean {
  return node instanceof Element && (node.tagName === 'TABLE' || !!node.querySelector('table'));
}

function inTable(node: Node): boolean {
  const element = node instanceof Element ? node : node.parentElement;
  return !!element?.closest('table');
}

/**
 * Whether a change in `main` can change a table's natural width or which tables show: a change
 * inside a table, a table added or removed, or a class, style or visibility change on a box that
 * holds one. Hover overlays, toasts and spinners beside the tables do not count, and neither do
 * the hook's own writes to `<main>`.
 */
function changesTables(record: MutationRecord, main: HTMLElement): boolean {
  if (record.type === 'attributes') {
    return record.target !== main && (inTable(record.target) || holdsTable(record.target));
  }
  if (inTable(record.target)) return true;
  return [...Array.from(record.addedNodes), ...Array.from(record.removedNodes)].some(holdsTable);
}

/**
 * Keeps a `fit` page as wide as its tables need while it is shown. It measures again before the
 * next paint when its tables change or a web font loads, and on the next frame when the shell's
 * page area changes width.
 */
export function usePageFitWidth(mainRef: RefObject<HTMLElement>, active: boolean): void {
  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!active || !main) return undefined;
    let frame = 0;
    let mainWidth = main.clientWidth;
    const fit = () => {
      frame = 0;
      fitPageWidth(main);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(fit);
    };
    // The page area's height follows the page, so only a change of width calls for a measure.
    const resizes = new ResizeObserver(() => {
      if (main.clientWidth === mainWidth) return;
      mainWidth = main.clientWidth;
      schedule();
    });
    const mutations = new MutationObserver((records) => {
      if (records.some((record) => changesTables(record, main))) schedule();
    });
    // jsdom has no FontFaceSet.
    const fonts = document.fonts as FontFaceSet | undefined;

    fit();
    resizes.observe(main);
    mutations.observe(main, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'open'],
    });
    fonts?.addEventListener('loadingdone', schedule);
    return () => {
      cancelAnimationFrame(frame);
      resizes.disconnect();
      mutations.disconnect();
      fonts?.removeEventListener('loadingdone', schedule);
      main.style.removeProperty(FIT_WIDTH_PROPERTY);
    };
  }, [active, mainRef]);
}
